<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset}" ref="observer">
    <artisio-modal scrollable size="lg" :visible="modal && modal.show" @hidden="onHidden" :content-in-form="false"
                   :title="$t('Condition images')" :hide-footer="true" modal-class="mobile-full-screen">
      <template v-if="!isReleased()">
        <file-uploader :show-preview="false" @onItemsAdd="onItemsAdd"
                       :items-prop="files" :hint-text="false"
                       :uploading-progress="uploadingProgress" :uploading-progress-value="uploadingProgressValue"
                       :accept="fileUploaderAccept"
                       :enable-image-resize="true"
                       :limit-upload-number="conditionReportImagesMaxAttachmentCount"
                       :limit-upload-total-size="conditionReportImagesAttachmentSizeSum"
                       ref="fileUploader"/>
      </template>
      <artisio-table :items="conditionReportImages"
                     :fields="tableFields"
                     :index="false">
        <template slot="cell(name)" slot-scope="{item: file}">
          <div class="d-flex justify-content-start align-items-center">
            <div style="width: 3em" class="text-center">
              <template v-if="file.content_type.includes('image/')">
                <b-img fluid :src="file.url"/>
              </template>
              <template v-else>
                <font-awesome-icon :icon="getIcon(file.content_type)" style="font-size: 2em;"
                                   :class="{'text-muted': file.content_type !== 'application/pdf', 'text-danger': file.content_type === 'application/pdf' }"/>
              </template>
            </div>
            <div class="ml-4">
              <b-button variant="link" class="p-0 text-left" @click="onPreviewClick(file)">{{ file.name }}</b-button>
            </div>
          </div>
        </template>
        <template slot="cell(created_at)" slot-scope="data">
          {{ data.item.created_at | toDateTime }}
        </template>
        <template slot="cell(created_by)" slot-scope="data">
          {{ data.item.created_by ? data.item.created_by.display_name : '' }}
        </template>
        <template slot="cell(actions)" slot-scope="{item: file}">
          <div style="min-height: 2em" class="d-flex align-items-center justify-content-center">
            <b-tooltip :target="`file-delete-${file.uuid}`" :title="$t('Delete')"/>
            <b-button :id="`file-delete-${file.uuid}`" variant="danger" size="sm"
                      @click="onFileRemoveClick(file)">
              <font-awesome-icon icon="times" class="icon"/>
            </b-button>
          </div>
        </template>
      </artisio-table>
      <br/>
    </artisio-modal>
    <attachment-view-modal/>
  </ValidationObserver>
</template>

<script>
import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal.vue";
import {createNamespacedHelpers} from "vuex";
import ActivityList from "@/modules/activities/ActivityList.vue";
import ArtisioTable from "@/core/components/ams-table/ArtisioTable.vue";
import FileUploader from "@/core/components/file-uploader/FileUploader.vue";
import i18n from "@/shared/i18n";
import AttachmentViewModal from "@/modules/attachments/AttachmentViewModal";
import CancelButton from "@/core/components/common/CancelButton.vue";

const {mapActions, mapState} = createNamespacedHelpers('conditionReportImages');
const {mapActions: attachmentsActions} = createNamespacedHelpers('attachments');
const {mapState: globalMapSate} = createNamespacedHelpers('global');

export default {
  name: "ConditionReportImagesModal",
  components: {CancelButton, FileUploader, ArtisioTable, AttachmentViewModal, ArtisioModal, ActivityList},
  data() {
    return {
      uploadingProgressValue: 0,
      uploadingProgress: false,
      files: [],
    }
  },
  computed: {
    ...mapState({
      object: state => state.imagesModal.object,
      modal: state => state.imagesModal,
      conditionReportImages: state => state.tableData.condition_images.rows,
    }),
    ...globalMapSate([
      'attachmentMaxFileSize',
      'imageFileFormats',
      'conditionReportImagesMaxAttachmentCount',
      'conditionReportImagesAttachmentSizeSum'
    ]),
    fileUploaderAccept() {
      return [{formats: this.imageFileFormats, size: this.attachmentMaxFileSize}]
    },
    tableFields() {
      return [
        { key: "name", label: i18n.t('File Name'), sortable: false },
        { key: "content_type", label: i18n.t('File Type'), sortable: false },
        { key: "created_by", label: i18n.t('Created by'), sortable: false },
        { key: "created_at", label: i18n.t('Creation Date'), sortable: false },
        ...(!this.isReleased()
            ? [{key: "actions", label: this.$i18n.t('Actions')}]
            : []),
      ];
    },
  },
  methods: {
    ...mapActions(['hideConditionReportImageModal', 'deleteConditionReportImage', 'createConditionReportImage']),
    ...attachmentsActions(['showViewModal', 'hideViewModal']),
    onHidden() {
      this.hideConditionReportImageModal();
      this.hideViewModal();
    },
    isReleased() {
      return this.object && this.object.status === 'released'
    },
    async onItemsAdd() {
      let formData = new FormData();
      this.files.forEach(item => formData.append('file', item.file))
      formData.append('condition_report_uuid', this.object.uuid)

      this.uploadingProgress = true;
      let response = await this.createConditionReportImage({
        formData, onUploadProgress: (progressEvent) => {
          this.uploadingProgressValue = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        }
      });
      if (response.success && response.body.length > 0) {
        this.clearData();
        this.$toast('success', this.$i18n.t('Files were uploaded successfully'));
      }
      if (response.body && response.body[0].invalid_images && response.body[0].invalid_images.length > 0 ) {

        let nonImageFiles = response.body[0].invalid_images;
        if (typeof nonImageFiles === 'string') {
          nonImageFiles = nonImageFiles.split('\n');
        }

        const listItems = nonImageFiles.map((file, index) => {
          return this.$createElement('li', { class: 'non-image-file' }, file);
        });

        const ulElement = this.$createElement('ul', { class: 'mb-0' }, listItems);

        const container = this.$createElement('div', { class: 'non-image-container' }, [
          this.$createElement('h5', { class: 'mb-0' }, 'Invalid image(s) provided for report'),
          ulElement,
        ]);

        this.$errorDialog(container, this.$i18n.t('Image Validation Error'));
        this.clearData();
      }
      this.uploadingProgress = false;
      this.uploadingProgressValue = 0;
    },
    getIcon(contentType) {
      if (contentType.includes('video/')) {
        return 'file-video';
      } else if (contentType.includes('audio/')) {
        return 'file-audio'
      } else if (contentType.includes('application/pdf')) {
        return 'file-pdf'
      }
    },
    onPreviewClick(file) {
      this.showViewModal(file);
    },

    clearData() {
      this.files = [];
      this.$refs.fileUploader.clearInput();
    },

    onFileRemoveClick(file) {
      this.$confirm(
          this.$i18n.t("Are you sure you want to delete following attachment?"),
          this.$i18n.t('Attachment was successfully deleted'),
          this.deleteConditionReportImage,
          file.uuid
      );
    },
  },
}
</script>
